import { useState, useEffect } from 'react';
import AvatarService from './AvatarService';

/**
 * Avatar component that uses S3 for storage
 */
function Avatar({ initialAvatarUrl = null, initialAvatarKey = null, onAvatarChange = null, size = 100 }) {
  const [avatarUrl, setAvatarUrl] = useState(initialAvatarUrl);
  const [avatarKey, setAvatarKey] = useState(initialAvatarKey);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // When initialAvatarUrl or initialAvatarKey changes, update the state
  useEffect(() => {
    if (initialAvatarUrl) {
      setAvatarUrl(initialAvatarUrl);
    }
    
    if (initialAvatarKey) {
      setAvatarKey(initialAvatarKey);
    }
  }, [initialAvatarUrl, initialAvatarKey]);
  
  // If we have a key but no URL, fetch the URL
  useEffect(() => {
    if (avatarKey && !avatarUrl) {
      const fetchAvatarUrl = async () => {
        try {
          const url = await AvatarService.getAvatarUrl(avatarKey);
          setAvatarUrl(url);
        } catch (err) {
          console.error('Failed to fetch avatar URL', err);
        }
      };
      
      fetchAvatarUrl();
    }
  }, [avatarKey, avatarUrl]);
  
  // Style for the avatar container
  const avatarStyle = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '4px',
    overflow: 'hidden',
    backgroundColor: '#f0f0f0',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    cursor: 'pointer',
  };
  
  // Style for the avatar image
  const imageStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  };
  
  // Style for the upload icon
  const uploadIconStyle = {
    position: 'absolute',
    bottom: '10px',
    right: '10px',
    width: `${size * 0.25}px`,
    height: `${size * 0.25}px`,
    backgroundColor: '#4B5563',
    borderRadius: '4px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: 'white',
    fontSize: `${size * 0.15}px`,
    border: '2px solid white',
  };
  
  // Style for the loading spinner
  const loadingStyle = {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.2)',
  };
  
  // Refresh the presigned URL every 45 minutes to ensure it doesn't expire
  useEffect(() => {
    if (!avatarKey) return;
    
    const refreshUrl = async () => {
      try {
        const url = await AvatarService.getAvatarUrl(avatarKey);
        setAvatarUrl(url);
      } catch (err) {
        console.error('Failed to refresh avatar URL', err);
        // Don't set error state here to avoid disrupting the UI
      }
    };
    
    // Only refresh if we don't already have a URL
    if (!avatarUrl) {
      refreshUrl();
    }
    
    // Refresh URL every 45 minutes (before the 1-hour expiration)
    const intervalId = setInterval(refreshUrl, 45 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, [avatarKey, avatarUrl]);
  
  /**
   * Handle file input change
   */
  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Read the file and convert to data URL
      const dataUrl = await AvatarService.readFileAsDataUrl(file);
      
      // Extract base64 data and content type
      const { base64Data, contentType } = AvatarService.extractBase64FromDataUrl(dataUrl);
      
      // Upload to S3
      const result = await AvatarService.uploadAvatar(base64Data, contentType);
      
      // Save the S3 key and URL
      setAvatarKey(result.key);
      setAvatarUrl(result.url);
      
      // Call the callback if provided
      if (onAvatarChange) {
        onAvatarChange({ key: result.key, url: result.url });
      }
    } catch (err) {
      console.error('Avatar upload failed', err);
      setError(err.message || 'Failed to upload avatar');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div>
      <div style={avatarStyle} title="Click to upload avatar">
        <input 
          type="file" 
          accept="image/*" 
          onChange={handleFileChange} 
          style={{ display: 'none' }}
          id="avatar-input"
          disabled={loading}
        />
        <label htmlFor="avatar-input" style={{ width: '100%', height: '100%', cursor: 'pointer' }}>
          {avatarUrl ? (
            <img src={avatarUrl} alt="Avatar" style={imageStyle} />
          ) : (
            <div style={{ 
              fontSize: `${size * 0.4}px`, 
              color: '#6B7280', 
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%'
            }}>
              <svg width="50%" height="50%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </div>
          )}
          
          {loading && (
            <div style={loadingStyle}>
              <svg width="40%" height="40%" viewBox="0 0 38 38" xmlns="http://www.w3.org/2000/svg" stroke="#fff">
                <g fill="none" fillRule="evenodd">
                  <g transform="translate(1 1)" strokeWidth="2">
                    <circle strokeOpacity=".5" cx="18" cy="18" r="18"/>
                    <path d="M36 18c0-9.94-8.06-18-18-18">
                      <animateTransform
                        attributeName="transform"
                        type="rotate"
                        from="0 18 18"
                        to="360 18 18"
                        dur="1s"
                        repeatCount="indefinite"/>
                    </path>
                  </g>
                </g>
              </svg>
            </div>
          )}
          
          {!loading && (
            <div style={uploadIconStyle}>
              <svg width="60%" height="60%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
            </div>
          )}
        </label>
      </div>
      
      {error && (
        <div style={{ color: 'red', marginTop: '5px', fontSize: '12px' }}>
          {error}
        </div>
      )}
    </div>
  );
}

export default Avatar; 